import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { useIsAnyCharityAdmin } from '@/state/profile';
import { useStickyCustomer } from '@/state/stickyCustomer';

type IconComponent = (props: React.SVGProps<SVGSVGElement>) => JSX.Element;

interface NavItem {
  to: string;
  prefix: string;
  label: string;
  icon: IconComponent;
}

export function BottomNav() {
  const { session } = useAuth();
  const showAdmin = useIsAnyCharityAdmin();
  const sticky = useStickyCustomer();
  const { pathname } = useLocation();
  if (!session) return null;

  const items: NavItem[] = [
    {
      to: sticky ? `/update?id=${sticky}` : '/update',
      prefix: '/update',
      label: 'Update',
      icon: UpdateIcon,
    },
    {
      to: sticky ? `/contact/${sticky}` : '/contact',
      prefix: '/contact',
      label: 'Contact',
      icon: ContactIcon,
    },
    { to: '/ledger', prefix: '/ledger', label: 'Ledger', icon: LedgerIcon },
    { to: '/search', prefix: '/search', label: 'Search', icon: SearchIcon },
  ];
  if (showAdmin) {
    items.push({ to: '/admin', prefix: '/admin', label: 'Admin', icon: AdminIcon });
  }

  return (
    <nav className="fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-ink-900 border-t border-ink-100 dark:border-ink-800 shadow-nav safe-bottom">
      <ul className="mx-auto max-w-3xl flex">
        {items.map(({ to, prefix, label, icon: Icon }) => {
          // Use a pathname-prefix match so the Update/Contact tabs stay
          // highlighted even when their `to` carries a specific customer id
          // different from the one currently in the URL.
          const active = pathname === prefix || pathname.startsWith(`${prefix}/`);
          return (
            <li key={prefix} className="flex-1">
              <NavLink
                to={to}
                end={false}
                className={[
                  'flex flex-col items-center justify-center gap-1 py-2 text-xs font-medium',
                  active ? 'text-accent' : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-100',
                ].join(' ')}
              >
                <Icon className="h-6 w-6" />
                <span>{label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function SearchIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function UpdateIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function ContactIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 2.06 4.18 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6 6l1.28-1.28a2 2 0 0 1 2.11-.45c.9.34 1.84.57 2.8.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function LedgerIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M4 4h12a2 2 0 0 1 2 2v14H6a2 2 0 0 1-2-2Z" />
      <path d="M4 4v14" />
      <path d="M8 8h6M8 12h6M8 16h4" />
    </svg>
  );
}

function AdminIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.55V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06A2 2 0 1 1 4.13 16.93l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.55-1.04H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.55-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06A2 2 0 1 1 7.07 4.13l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06A2 2 0 1 1 19.87 7.07l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1Z" />
    </svg>
  );
}
